import { agentApiHeaders, apiBase, chatApiRequest } from '../agentApi'
import axios from '../axiosInstance'
import {
  AgentListResponse,
  AgentMutationResponse,
  AgentPayload,
  AiAgent,
  ChatApp,
  ListAgentsOptions,
} from './types'

/**
 * AI Agent Builder CRUD. Unlike the legacy flat services, these REJECT on failure
 * rather than resolving an `{ err_code }` object — the sagas branch on `catch`.
 */

export const DEFAULT_AGENT_PAGE_SIZE = 6

const AGENTS_PATH = 'ai-agents/agent-builder/agents'

const agentsBase = (app: ChatApp): string => `${apiBase(app)}/${AGENTS_PATH}`

export async function list(
  app: ChatApp,
  { limit = DEFAULT_AGENT_PAGE_SIZE, search = '', cursorParams = '' }: ListAgentsOptions = {},
): Promise<AgentListResponse> {
  const params = [`limit=${limit}`]
  if (search.trim()) params.push(`search=${encodeURIComponent(search.trim())}`)
  if (cursorParams) params.push(cursorParams)
  // Cache-buster: a proxy in front of the API can otherwise serve a stale list.
  params.push(`_t=${Date.now()}`)

  return chatApiRequest<AgentListResponse>(() =>
    axios.get(`${agentsBase(app)}?${params.join('&')}`, {
      headers: { ...agentApiHeaders(app), 'Cache-Control': 'no-cache' },
    }),
  )
}

export async function get(app: ChatApp, agentId: string): Promise<AiAgent> {
  return chatApiRequest<AiAgent>(() =>
    axios.get(`${agentsBase(app)}/${agentId}`, { headers: agentApiHeaders(app) }),
  )
}

export async function add(app: ChatApp, agentData: AgentPayload): Promise<AgentMutationResponse> {
  return chatApiRequest<AgentMutationResponse>(() =>
    axios.post(agentsBase(app), agentData, { headers: agentApiHeaders(app) }),
  )
}

export async function update(
  app: ChatApp,
  agentId: string,
  agentData: Partial<AgentPayload> & { isActive?: boolean },
): Promise<AgentMutationResponse> {
  return chatApiRequest<AgentMutationResponse>(() =>
    axios.patch(`${agentsBase(app)}/${agentId}`, agentData, {
      headers: agentApiHeaders(app),
    }),
  )
}

export async function remove(app: ChatApp, agentId: string): Promise<AgentMutationResponse> {
  return chatApiRequest<AgentMutationResponse>(() =>
    axios.delete(`${agentsBase(app)}/${agentId}`, { headers: agentApiHeaders(app) }),
  )
}
